import { amountFormatter } from '/src/lib/helpers';
import { BALANCE_SHEET_FUND_TYPE, BALANCE_SHEET_LABEL } from './constants';

const MISSING_VALUE = 'N/A';

const isMissingValue = (value) =>
    value === undefined || value === null || value === '';

// ── Amount helpers ─────────────────────────────────────────────────────────────
export const getAmountValue = (amount) => {
    if (!amount) return 'Undisclosed';
    return amountFormatter(amount);
};

const getDeploymentColor = (pct) => {
    if (pct == null) return 'grey';
    if (pct < 30) return 'red';
    if (pct < 65) return 'orange';
    return 'green';
};

// ── Status helpers ─────────────────────────────────────────────────────────────
// Returns array of { label, variant } badge descriptors
const getStatusBadges = (fund = {}) => {
    const badges = [];

    // Closed / Open
    if (!isMissingValue(fund.fund_status)) {
        badges.push({
            label: fund.fund_status,
            variant: String(fund.fund_status).toLowerCase() === 'closed' ? 'closed' : 'open',
        });
    }

    // Active / Deployed / etc.
    if (!isMissingValue(fund.deployment_status)) {
        badges.push({
            label: fund.deployment_status,
            variant: String(fund.deployment_status).toLowerCase() === 'active' ? 'active' : 'deployed',
        });
    }

    return badges;
};

// ── Row builder (getFundsForInvestor fund shape) ───────────────────────────────
export const buildManagedFundRow = (fund = {}) => {
    // Balance sheet isn't a real fund — vintage, size, deployment % and status show as dash, but its deployed capital still shows
    const isBalanceSheet = Number(fund.is_balance_sheet) === BALANCE_SHEET_FUND_TYPE;
    const rawSize = fund.target_fund_size ?? fund.final_close_amount ?? fund.first_close_amount ?? null;
    const fundSize = (rawSize == null || rawSize === '' || Number(rawSize) === 0) ? null : rawSize;
    const rawDeployed = fund.direct_estimated_deployed_capital ?? null;
    const deployed = (rawDeployed == null || rawDeployed === '' || Number(rawDeployed) === 0) ? null : rawDeployed;
    const isDeployedEstimated = deployed != null;

    const rawPct = fund.direct_estimated_deployed_capital_percentage ?? null;
    const deploymentPct = (rawPct != null && Number(rawPct) !== 0)
        ? Math.min(Math.round(Number(rawPct)), 100)
        : null;
    const isDeploymentPctEstimated = deploymentPct != null;
    const deploymentColor = getDeploymentColor(deploymentPct);

    const lpRows = (fund.investors_data || []).map((inv) => ({
        id: inv.id || null,
        code: inv.code || null,
        name: inv.name || null,
        hq: inv.hq || null,
        type: inv.investor_type || null,
        entityType: inv.entity_type || null,
        isLead: inv.is_lead === 1 || inv.is_lead === true,
        amount: (inv.invested_amount != null && Number(inv.invested_amount) !== 0) ? inv.invested_amount : null,
        estimatedAmount: (inv.estimated_invested_amount != null && Number(inv.estimated_invested_amount) !== 0) ? inv.estimated_invested_amount : null,
    }));

    const fundraisingLabel = fund.fundraising_status == null ? null
        : Number(fund.fundraising_status) === 1 ? 'Raising' : 'Closed';
    const investmentLabel = fund.investment_status == null ? null
        : Number(fund.investment_status) === 1 ? 'Active' : 'Deployed';

    // Balance sheet has no fund lifecycle, so it gets no status badges
    const statusBadges = isBalanceSheet ? [] : getStatusBadges({
        fund_status: fundraisingLabel,
        deployment_status: investmentLabel,
    });

    return {
        id: fund.id,
        name: isBalanceSheet ? BALANCE_SHEET_LABEL : (fund.name || MISSING_VALUE),
        isBalanceSheet,
        vintage: (!isBalanceSheet && !isMissingValue(fund.fund_vintage)) ? fund.fund_vintage : null,
        fundSize: isBalanceSheet ? null : fundSize,
        deployed,
        isDeployedEstimated,
        deploymentPct: isBalanceSheet ? null : deploymentPct,
        isDeploymentPctEstimated,
        deploymentColor,
        portfolioCompanies: (!isMissingValue(fund.companies_investedin_count) && Number(fund.companies_investedin_count) !== 0) ? Number(fund.companies_investedin_count) : null,
        exitsCount: (!isMissingValue(fund.number_of_exited_investments) && Number(fund.number_of_exited_investments) !== 0) ? Number(fund.number_of_exited_investments) : null,
        moicDisplay: null,
        lpCount: (!isBalanceSheet && lpRows.length > 0) ? lpRows.length : null,
        lpRows,
        statusBadges,
        isVerified: fund.investedin_fund_verification_status === 1,
        profileUrl: '',
    };
};

// Balance sheet isn't ranked against funds, so it stays pinned below them whatever the sort
export const pinBalanceSheetLast = (rows = []) => [
    ...rows.filter((row) => !row.isBalanceSheet),
    ...rows.filter((row) => row.isBalanceSheet),
];

// ── Summary stats from getManagedFundAggregation response ─────────────────────
// All calculations (pct_deployed, est_dry_powder, vintage_range) are performed
// by the backend. This function only applies display formatting (amountFormatter).
//
// Expected aggregation shape from backend:
// {
//   total_funds, total_amount_raised, total_capital_deployed,
//   pct_deployed, est_dry_powder, vintage_range
// }
export const computeFundSummaryStatsFromAggregation = (agg = {}) => {
    const totalTargetFundSize = Number(agg.total_target_fund_size || 0);
    const totalRaised = Number(agg.total_amount_raised?.amount || 0);
    const totalRaisedHasProxy = Boolean(agg.total_amount_raised?.has_proxy);
    const totalDeployed = Number(agg.total_capital_deployed || 0);
    const estDryPowder = Number(agg.est_dry_powder || 0);

    return {
        totalFunds: Number(agg.total_funds || 0),
        vintageRange: agg.vintage_range || null,
        totalTargetFundSize: totalTargetFundSize > 0 ? amountFormatter(totalTargetFundSize) : null,
        totalCapitalRaised: totalRaised > 0 ? amountFormatter(totalRaised) : null,
        totalCapitalRaisedHasProxy: totalRaisedHasProxy,
        capitalDeployed: totalDeployed > 0 ? amountFormatter(totalDeployed) : null,
        pctDeployed: agg.pct_deployed != null ? Number(agg.pct_deployed) : null,
        estDryPowder: estDryPowder > 0 ? amountFormatter(estDryPowder) : null,
    };
};

